/**
 * Enforce minimum cart quantity for flooring-tiles products.
 * Min sources (first match wins): data-min-qty on line item, input[min], TILE_CART_MIN_QTY + flooring markers.
 * Uses live cart state when available (theme +/- uses that, not the input).
 * Capture-phase so theme cart handlers never apply an invalid decrement.
 * Minus button is disabled + faded at minimum, enabled + full opacity above min.
 */
(function () {
  const ITEM_SELECTOR = '[data-cart-item], .scd-item';
  const QTY_WRAP_SELECTOR = '.scd-item__quantity, .scd-item__qty';
  const DEFAULT_MIN = 10;
  const DISABLED_CLASS = 'scd-item__btn--at-min';

  function getFallbackMin() {
    const fromWindow = Number(window.TILE_CART_MIN_QTY);
    return Number.isFinite(fromWindow) && fromWindow > 0 ? fromWindow : DEFAULT_MIN;
  }

  function getMinQty(el) {
    const item = el.closest(ITEM_SELECTOR);
    if (!item) return 1;

    const fromData = Number(item.dataset.minQty);
    if (Number.isFinite(fromData) && fromData > 1) return fromData;

    const inputs = item.querySelectorAll('.scd-item__qty_input');
    for (const input of inputs) {
      const fromInput = Number(input.min);
      if (Number.isFinite(fromInput) && fromInput > 1) return fromInput;
    }

    if (item.dataset.flooringTiles === 'true') {
      return getFallbackMin();
    }

    return 1;
  }

  function getQtyInputs(fromEl) {
    const item = fromEl.closest(ITEM_SELECTOR);
    if (!item) return [];
    return Array.from(item.querySelectorAll('.scd-item__qty_input'));
  }

  function getNearbyQtyInput(fromEl) {
    const wrap = fromEl.closest(QTY_WRAP_SELECTOR);
    if (wrap) {
      const input = wrap.querySelector('.scd-item__qty_input');
      if (input) return input;
    }
    return getQtyInputs(fromEl)[0] || null;
  }

  function getCurrentQty(fromEl) {
    const id = fromEl.dataset?.id || fromEl.closest(ITEM_SELECTOR)?.dataset?.id;
    const cartItems = window.MinimogTheme?.Cart?.cart?.items;
    if (id && Array.isArray(cartItems)) {
      const line = cartItems.find((i) => i.key === id);
      if (line && Number.isFinite(Number(line.quantity))) {
        return Number(line.quantity);
      }
    }

    const input = getNearbyQtyInput(fromEl);
    if (input) {
      const n = Number(input.value);
      if (Number.isFinite(n)) return n;
    }
    return null;
  }

  function syncQtyInputs(fromEl, value) {
    getQtyInputs(fromEl).forEach((input) => {
      input.value = value;
    });
  }

  function setMinusButtonState(btn, atMin) {
    if (!btn) return;
    btn.disabled = atMin;
    btn.setAttribute('aria-disabled', atMin ? 'true' : 'false');
    btn.classList.toggle(DISABLED_CLASS, atMin);
    if (atMin) {
      btn.setAttribute('title', 'Minimum order quantity reached');
    } else {
      btn.removeAttribute('title');
    }
  }

  function updateItemMinusButtons(item) {
    if (!item) return;
    const min = getMinQty(item);
    if (min <= 1) {
      item.querySelectorAll('.scd-item__btn[data-qty-change="dec"]').forEach((btn) => {
        setMinusButtonState(btn, false);
      });
      return;
    }

    const current = getCurrentQty(item);
    if (current === null) return;

    const atMin = current <= min;
    item.querySelectorAll('.scd-item__btn[data-qty-change="dec"]').forEach((btn) => {
      setMinusButtonState(btn, atMin);
    });
  }

  function updateAllMinusButtons(root = document) {
    root.querySelectorAll(ITEM_SELECTOR).forEach(updateItemMinusButtons);
  }

  document.addEventListener(
    'click',
    function (e) {
      const btn = e.target.closest('.scd-item__btn');
      if (!btn || btn.dataset.qtyChange !== 'dec') return;

      const min = getMinQty(btn);
      if (min <= 1) return;

      const current = getCurrentQty(btn);
      if (current === null) return;

      if (current <= min || btn.disabled) {
        e.preventDefault();
        e.stopImmediatePropagation();
        syncQtyInputs(btn, min);
        updateItemMinusButtons(btn.closest(ITEM_SELECTOR));
        return;
      }

      // After theme updates cart, re-sync minus state (section re-render may lag)
      window.setTimeout(() => {
        updateItemMinusButtons(btn.closest(ITEM_SELECTOR));
        updateAllMinusButtons();
      }, 50);
      window.setTimeout(updateAllMinusButtons, 400);
      window.setTimeout(updateAllMinusButtons, 900);
    },
    true
  );

  // Also re-check after + clicks / qty input changes
  document.addEventListener(
    'click',
    function (e) {
      const btn = e.target.closest('.scd-item__btn');
      if (!btn || btn.dataset.qtyChange !== 'inc') return;
      window.setTimeout(updateAllMinusButtons, 50);
      window.setTimeout(updateAllMinusButtons, 400);
      window.setTimeout(updateAllMinusButtons, 900);
    },
    true
  );

  document.addEventListener(
    'change',
    function (e) {
      const input = e.target.closest('.scd-item__qty_input');
      if (!input) return;

      const min = getMinQty(input);
      if (min <= 1) {
        updateItemMinusButtons(input.closest(ITEM_SELECTOR));
        return;
      }

      const val = Number(input.value);
      if (!Number.isFinite(val)) return;

      if (val > 0 && val < min) {
        input.value = min;
        syncQtyInputs(input, min);
      }

      updateItemMinusButtons(input.closest(ITEM_SELECTOR));
      window.setTimeout(updateAllMinusButtons, 400);
    },
    true
  );

  document.addEventListener(
    'submit',
    function (e) {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.classList.contains('sf-cart__form') && !form.querySelector('.scd-item__qty_input')) return;

      form.querySelectorAll(ITEM_SELECTOR).forEach((item) => {
        const min = getMinQty(item);
        if (min <= 1) return;
        item.querySelectorAll('.scd-item__qty_input').forEach((input) => {
          const val = Number(input.value);
          if (Number.isFinite(val) && val > 0 && val < min) {
            input.value = min;
          }
        });
        updateItemMinusButtons(item);
      });
    },
    true
  );

  function observeCartDom() {
    const targets = document.querySelectorAll('.scd__wrapper, .scd__body, .scd__items, #cart-drawer-container');
    if (!targets.length) return;

    const observer = new MutationObserver(() => {
      window.requestAnimationFrame(() => updateAllMinusButtons());
    });

    targets.forEach((target) => {
      observer.observe(target, { childList: true, subtree: true });
    });
  }

  function init() {
    updateAllMinusButtons();
    observeCartDom();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme cart may load async
  window.setTimeout(updateAllMinusButtons, 300);
  window.setTimeout(updateAllMinusButtons, 1000);

  if (window.MinimogEvents?.subscribe) {
    window.MinimogEvents.subscribe('ON_CART_UPDATE', () => {
      window.setTimeout(updateAllMinusButtons, 50);
      window.setTimeout(updateAllMinusButtons, 400);
    });
  }
})();
